// Slider.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// CSS
import './Slider.css';

const clamp = (value, min, max) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return min;
  if (numeric < min) return min;
  if (numeric > max) return max;
  return numeric;
};

const snapValue = (value, snapTo, min, max) => {
  const step = Number.isFinite(Number(snapTo)) && Number(snapTo) > 0 ? Number(snapTo) : 1;
  const snapped = Math.round(clamp(value, min, max) / step) * step;
  return clamp(Math.round(snapped), min, max);
};

/**
 * Slider
 *
 * Renders a themed range slider with optional snapped value display.
 *
 * @param {number} value - Current slider value.
 * @param {number} min - Minimum slider value.
 * @param {number} max - Maximum slider value.
 * @param {number} snapTo - Snap interval for emitted/display values.
 * @param {function} onChange - Callback fired with snapped value updates.
 * @param {boolean} disabled - Whether the slider is disabled.
 * @param {boolean} showValue - Whether to render the numeric value label.
 * @param {string} suffix - Suffix appended to displayed value.
 * @param {string} className - Additional wrapper class names.
 *
 * @returns {ReactNode} The rendered component.
 */
const Slider = ({
  value = 0,
  min = 0,
  max = 100,
  snapTo = 5,
  onChange,
  disabled = false,
  showValue = true,
  suffix = '',
  className = '',
}) => {

  // ----- CONTEXT ----- //

  // Current clamped external value.
  const clampedValue = clamp(value, min, max);

  // ----- STATE ----- //

  // Raw slider position while interacting.

  const [rawValue, setRawValue] = useState(clampedValue);

  // Drag state flag.
  const [isDragging, setIsDragging] = useState(false);

  // ----- REFS ----- //

  // Last emitted snapped value reference.

  const lastEmittedRef = useRef(snapValue(clampedValue, snapTo, min, max));

  // Draggable ancestor temporarily disabled during pointer drag.
  const disabledDragAncestorRef = useRef(null);

  // ----- MEMOS ----- //

  // Snapped value used for value readout.

  const snappedDisplay = useMemo(
    () => snapValue(rawValue, snapTo, min, max),
    [rawValue, snapTo, min, max]
  );

  // Wrapper class list.
  const wrapperClassName = ['slider', className].filter(Boolean).join(' ');

  // ----- EFFECTS ----- //

  // Keep raw value synced with external value when not dragging.

  useEffect(() => {
    if (!isDragging) {
      setRawValue(clampedValue);
    }
  }, [clampedValue, isDragging]);

  // Restore draggable ancestor on unmount.
  useEffect(() => () => {
    restoreNearestDraggableAncestor();
  }, []);

  // ----- UTILITIES ----- //

  /**
   * Emits a snapped value when it changes.
   *
   * @param {number|string} nextRawValue - Raw slider value.
   */

  const emitValue = (nextRawValue) => {
    if (typeof onChange !== 'function') return;
    const nextSnapped = snapValue(nextRawValue, snapTo, min, max);
    if (nextSnapped === lastEmittedRef.current) return;
    lastEmittedRef.current = nextSnapped;
    onChange(nextSnapped);
  };

  // ----- HANDLERS ----- //

  /**
   * Handles native range value changes.
   *
   * @param {Event} event - Input change event.
   */

  const handleChange = (event) => {
    const nextRawValue = clamp(event.target.value, min, max);
    setRawValue(nextRawValue);
    emitValue(nextRawValue);
  };

  // ----- UTILITIES ----- //

  /**
   * Stops event propagation for slider interactions.
   *
   * @param {Event} event - Browser event.
   */

  const blockEvent = (event) => {
    if (!event) return;
    event.stopPropagation();
  };

  /**
   * Disables the nearest draggable ancestor while dragging.
   *
   * @param {Event} event - Pointer event.
   */
  const disableNearestDraggableAncestor = (event) => {
    const target = event?.target;
    if (!(target instanceof Element)) return;

    const draggableAncestor = target.closest('[draggable="true"]');
    if (!draggableAncestor || !(draggableAncestor instanceof HTMLElement)) return;
    if (disabledDragAncestorRef.current === draggableAncestor) return;

    draggableAncestor.setAttribute('data-slider-draggable-disabled', 'true');
    draggableAncestor.setAttribute('draggable', 'false');
    disabledDragAncestorRef.current = draggableAncestor;
  };

  /**
   * Restores draggable state on previously disabled ancestor.
   */
  const restoreNearestDraggableAncestor = () => {
    const node = disabledDragAncestorRef.current;
    if (!node) return;

    if (node instanceof HTMLElement && node.getAttribute('data-slider-draggable-disabled') === 'true') {
      node.setAttribute('draggable', 'true');
      node.removeAttribute('data-slider-draggable-disabled');
    }

    disabledDragAncestorRef.current = null;
  };

  // ----- HANDLERS ----- //

  /**
   * Starts dragging interactions and disables ancestor dragging.
   *
   * @param {Event} event - Pointer down event.
   */

  const handlePointerDown = (event) => {
    blockEvent(event);
    disableNearestDraggableAncestor(event);
    setIsDragging(true);
  };

  /**
   * Ends drag interactions and restores ancestor dragging.
   *
   * @param {Event} event - Pointer up/cancel event.
   */
  const handlePointerUpOrCancel = (event) => {
    blockEvent(event);
    setIsDragging(false);
    restoreNearestDraggableAncestor();
  };

  /**
   * Prevents native drag behavior from starting.
   *
   * @param {Event} event - Drag start event.
   */
  const handleNativeDragStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // ----- RENDER ----- //

  return (
    <div className={wrapperClassName} data-no-field-drag="true" onDragStart={handleNativeDragStart}>
      {/* INPUT */}
      <input
        className="slider-input"
        type="range"
        value={rawValue}
        min={min}
        max={max}
        step={1}
        disabled={disabled}
        onChange={handleChange}
        onClick={blockEvent}
        onDragStart={handleNativeDragStart}
        onPointerCancel={handlePointerUpOrCancel}
        onPointerDown={handlePointerDown}
        onPointerMove={blockEvent}
        onPointerUp={handlePointerUpOrCancel}
      />

      {showValue && (
        <>
        {/* VALUE */}
        <div
          className="slider-value"
          aria-live="polite"
        >
          {snappedDisplay}
          {suffix}
        </div>
        </>
      )}
    </div>
  );
};

// Dev Tools
Slider.displayName = 'Slider';

export default Slider;
